import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@kiku/i18n';
import { INTERVIEW_MODALITY } from '@kiku/interviews/types';
import type { InterviewModality } from '@kiku/interviews/types';
import { Search, MessageSquare, Phone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';

const MODALITY_LABELS: Record<
  InterviewModality,
  'wizard.basicInfo.modalityText' | 'wizard.basicInfo.modalityVoice'
> = {
  text: 'wizard.basicInfo.modalityText',
  voice: 'wizard.basicInfo.modalityVoice'
};

const MODALITY_DESCRIPTIONS: Record<
  InterviewModality,
  'wizard.basicInfo.modalityTextDesc' | 'wizard.basicInfo.modalityVoiceDesc'
> = {
  text: 'wizard.basicInfo.modalityTextDesc',
  voice: 'wizard.basicInfo.modalityVoiceDesc'
};

const MODALITY_ICONS: Record<InterviewModality, React.ReactNode> = {
  text: <MessageSquare className="size-5" />,
  voice: <Phone className="size-5" />
};

type InterviewFormatFieldsProps = {
  selectedModalities: Array<InterviewModality>;
  onModalityToggle: (m: InterviewModality) => void;
  modalityError?: boolean;
  selectedLanguages: string[];
  onLanguageToggle: (code: string) => void;
  languagesError?: boolean;
};

export function InterviewFormatFields({
  selectedModalities,
  onModalityToggle,
  modalityError,
  selectedLanguages,
  onLanguageToggle,
  languagesError
}: InterviewFormatFieldsProps): React.ReactNode {
  const { t } = useTranslation();
  const [languageSearch, setLanguageSearch] = useState('');

  const filteredLanguages = SUPPORTED_LANGUAGES.filter((code: SupportedLanguage) => {
    const label = (t as (k: string) => string)(`screening.languages.${code as string}`);
    return label.toLowerCase().includes(languageSearch.toLowerCase());
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label>{t('wizard.basicInfo.modality')}</Label>
        <div className="flex flex-col gap-3 sm:flex-row">
          {INTERVIEW_MODALITY.map((m) => (
            <label
              key={m}
              className="flex flex-1 cursor-pointer items-center gap-3 rounded-md border border-border px-4 py-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <span className="shrink-0 text-muted-foreground">{MODALITY_ICONS[m]}</span>
              <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                <span className="text-sm font-medium">{t(MODALITY_LABELS[m])}</span>
                <span className="text-xs text-muted-foreground">{t(MODALITY_DESCRIPTIONS[m])}</span>
              </div>
              <input
                type="checkbox"
                checked={selectedModalities.includes(m)}
                onChange={() => {
                  onModalityToggle(m);
                }}
                className="shrink-0 accent-primary"
              />
            </label>
          ))}
        </div>
        <p className={`text-sm text-destructive ${modalityError ? 'visible' : 'invisible'}`}>
          {t('wizard.basicInfo.modalityRequired')}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t('wizard.basicInfo.languages')}</Label>
        <div
          className={`rounded-md border bg-card ${languagesError ? 'border-destructive' : 'border-input'}`}
        >
          <div className="relative border-b border-border">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="border-0 pl-8 shadow-none focus-visible:ring-0"
              placeholder={t('wizard.basicInfo.languagesSearch')}
              value={languageSearch}
              onChange={(e) => {
                setLanguageSearch(e.target.value);
              }}
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredLanguages.map((code: SupportedLanguage) => (
              <label
                key={code}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent has-[:checked]:bg-primary/5"
              >
                <input
                  type="checkbox"
                  checked={selectedLanguages.includes(code)}
                  onChange={() => {
                    onLanguageToggle(code);
                  }}
                  className="accent-primary"
                />
                {(t as (k: string) => string)(`screening.languages.${code as string}`)}
              </label>
            ))}
            {filteredLanguages.length === 0 && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                {t('wizard.basicInfo.languagesEmpty')}
              </p>
            )}
          </div>
        </div>
        <p className={`text-sm text-destructive ${languagesError ? 'visible' : 'invisible'}`}>
          {t('wizard.basicInfo.languagesRequired')}
        </p>
      </div>
    </>
  );
}
